/**
 * Filename utilities for documents
 */

const MAX_NAME_LENGTH = 50;
// biome-ignore lint/suspicious/noControlCharactersInRegex: intentionally matching control characters for filename sanitization
const INVALID_CHARS_REGEX = /[<>:"/\\|?*\x00-\x1f]/g;
const DEFAULT_NAME = 'Untitled';

/**
 * Extract the first H1 heading from Markdown content
 */
export function extractHeading(content: string): string | null {
    const match = content.match(/^#\s+(.+)$/m);
    if (!match?.[1]) return null;

    // Clean up the heading
    return match[1]
        .trim()
        .replace(/[#*_`[\]]/g, '') // Remove markdown characters
        .trim();
}

/**
 * Sanitize a filename by removing invalid characters
 */
export function sanitizeFilename(name: string): string {
    let sanitized = name
        .replace(INVALID_CHARS_REGEX, '') // Remove invalid characters
        .replace(/[*_`~#]/g, '') // Remove markdown formatting
        .replace(/\s+/g, ' ') // Normalize whitespace
        .trim();

    // Truncate if too long
    if (sanitized.length > MAX_NAME_LENGTH) {
        sanitized = sanitized.substring(0, MAX_NAME_LENGTH).trim();
        // Don't end with partial word
        const lastSpace = sanitized.lastIndexOf(' ');
        if (lastSpace > MAX_NAME_LENGTH - 10) {
            sanitized = sanitized.substring(0, lastSpace);
        }
    }

    return sanitized || DEFAULT_NAME;
}

/**
 * Generate a filename from content or use default
 */
export function generateFilename(content: string, defaultName = DEFAULT_NAME): string {
    const heading = extractHeading(content);
    if (heading) {
        return sanitizeFilename(heading);
    }

    // Try first non-empty line
    const lines = content.split('\n');
    for (const line of lines) {
        const trimmed = line.trim();
        if (trimmed && !trimmed.startsWith('---')) {
            return sanitizeFilename(trimmed);
        }
    }

    return defaultName;
}

/**
 * Validate a document name
 */
export function validateFilename(name: string): { valid: boolean; error?: string } {
    if (!name || !name.trim()) {
        return { valid: false, error: 'Name cannot be empty' };
    }

    const trimmed = name.trim();

    if (trimmed.length > MAX_NAME_LENGTH) {
        return { valid: false, error: `Name too long (max ${MAX_NAME_LENGTH} characters)` };
    }

    if (INVALID_CHARS_REGEX.test(trimmed)) {
        return { valid: false, error: 'Name contains invalid characters' };
    }

    return { valid: true };
}

/**
 * Check if name looks auto-generated
 */
export function isAutoGeneratedName(name: string): boolean {
    return name === DEFAULT_NAME || name.startsWith('Untitled ');
}

/**
 * Generate unique name
 */
export function generateUniqueName(baseName: string, existingNames: string[]): string {
    if (!existingNames.includes(baseName)) {
        return baseName;
    }

    let counter = 1;
    let newName = `${baseName} ${counter}`;

    while (existingNames.includes(newName)) {
        counter++;
        newName = `${baseName} ${counter}`;
    }

    return newName;
}

/**
 * Get file extension
 */
export function getExtension(filename: string): string {
    const lastDot = filename.lastIndexOf('.');
    if (lastDot === -1) return '';
    return filename.substring(lastDot + 1).toLowerCase();
}

/**
 * Remove file extension
 */
export function removeExtension(filename: string): string {
    const lastDot = filename.lastIndexOf('.');
    if (lastDot === -1) return filename;
    return filename.substring(0, lastDot);
}

/**
 * Ensure .md extension
 */
export function ensureMarkdownExtension(filename: string): string {
    const ext = getExtension(filename);
    if (ext === 'md' || ext === 'markdown') {
        return filename;
    }
    return `${removeExtension(filename)}.md`;
}
